import {useEffect, useRef} from 'react';

/**
 * Nút Back của điện thoại đóng lớp phủ thay vì rời trang.
 * ---------------------------------------------------------------------------
 * Modal chỉ bắt phím Escape - mà trên điện thoại, thiết bị chính của app này,
 * không có Escape. Người dùng gặp một lớp phủ toàn màn hình thì phản xạ là bấm
 * Back, và trước đây cú bấm đó rời hẳn trang (hoặc thoát PWA) thay vì đóng lớp
 * phủ. Thẻ phòng ở /diennuoc mở tới bốn lớp phủ nên tình huống này gặp liên tục.
 *
 * Cách làm: mở lớp phủ thì đẩy một entry rỗng vào lịch sử duyệt để Back có cái
 * mà nuốt. Đóng bằng cách khác (nút X, Escape, bấm ra ngoài) thì tự gỡ entry đó.
 *
 * `overlayStack` là danh sách dấu của các lớp phủ đang mở, đáy lên đỉnh. Cần một
 * biến dùng chung ở mức module vì `popstate` là sự kiện của `window`: MỌI lớp
 * phủ đang mở đều nghe thấy nó. Không có ngăn xếp thì một cú Back đóng sạch cả
 * chồng - trong khi thư viện hóa đơn mở lightbox chồng lên là chuyện bình
 * thường, và Back ở đó phải đóng đúng lightbox rồi để lại thư viện.
 */
const MARKER_KEY = 'nha48Overlay';
const overlayStack: string[] = [];

const topOfHistory = (): string | undefined => window.history.state?.[MARKER_KEY];

export const useOverlayBackButton = (isOpen: boolean, onClose: () => void) => {
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    /* `onClose` đọc qua ref. Gần như mọi nơi gọi đều truyền một hàm mũi tên tạo
       mới mỗi lần render; để nó vào mảng phụ thuộc thì effect tháo/lắp liên tục,
       tức là đẩy rồi gỡ entry lịch sử vài lần mỗi giây. */

    // Cần biết lớp phủ CÒN mở không sau khi đã gọi onClose - xem bẫy 3
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    useEffect(() => {
        if (!isOpen) return;

        const marker = `overlay-${Date.now()}-${Math.random().toString(36).slice(2)}`;

        const stamp = (mode: 'push' | 'replace') => {
            // Giữ nguyên state của react-router (`usr`, `key`, `idx`) - ghi đè
            // bằng object rỗng thì router mất chỉ số và tính sai bước lùi
            const state = {...window.history.state, [MARKER_KEY]: marker};
            if (mode === 'push') window.history.pushState(state, '');
            else window.history.replaceState(state, '');
        };

        /* BẪY 1: đóng lớp phủ này để mở lớp phủ kia.
           App làm việc này ở ba chỗ (thẻ phòng -> lịch sử, hóa đơn -> lịch sử).
           Lúc đó lớp cũ đã dọn dẹp xong nhưng entry của nó vẫn nằm trên đỉnh -
           `history.back()` là thao tác BẤT ĐỒNG BỘ nên chưa kịp chạy. Ngăn xếp
           rỗng mà lịch sử vẫn còn dấu nghĩa là đúng tình huống đó: nhận lại
           entry mồ côi thay vì chồng thêm cái nữa. Không làm vậy thì người dùng
           phải bấm Back hai lần mới ra khỏi trang. */
        const orphan = overlayStack.length === 0 && topOfHistory() !== undefined;
        stamp(orphan ? 'replace' : 'push');
        overlayStack.push(marker);

        let owns = true;
        let recheck: ReturnType<typeof setTimeout> | undefined;

        const release = () => {
            const at = overlayStack.lastIndexOf(marker);
            if (at !== -1) overlayStack.splice(at, 1);
        };

        const handlePopState = () => {
            /* BẪY 2: chỉ lớp TRÊN CÙNG được đóng. `popstate` bắn tới listener
               của mọi lớp đang mở, nên lớp nằm dưới phải im lặng - nếu không
               một cú Back đóng sạch cả chồng. */
            if (overlayStack[overlayStack.length - 1] !== marker) return;

            release();
            owns = false; // trình duyệt vừa nuốt entry - không được gỡ lần nữa
            onCloseRef.current();

            /* BẪY 3: nơi gọi có quyền TỪ CHỐI đóng - bảng nhập hàng loạt hỏi
               "chỉ số vừa gõ sẽ mất, đóng chứ?". Người dùng bấm Huỷ thì lớp phủ
               còn đó nhưng entry đã mất, phải đẩy lại một cái. Đợi một nhịp cho
               React vẽ xong rồi mới biết nó có thật sự đóng hay không. */
            recheck = setTimeout(() => {
                if (!isOpenRef.current) return;
                stamp('push');
                overlayStack.push(marker);
                owns = true;
            }, 0);
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
            if (recheck) clearTimeout(recheck);
            release();
            if (!owns) return;

            /* Hoãn một nhịp trước khi gỡ entry: nếu ngay sau đây có lớp phủ khác
               mở ra (bẫy 1), nó sẽ nhận lại entry này và ta không được lùi nữa.
               Kiểm tra cả dấu trên đỉnh: người dùng có thể vừa bấm một liên kết
               BÊN TRONG lớp phủ, lúc đó router đã đẩy entry mới và lùi ở đây là
               lùi nhầm một trang thật. */
            setTimeout(() => {
                if (overlayStack.length > 0) return;
                if (topOfHistory() !== marker) return;
                window.history.back();
            }, 0);
        };
    }, [isOpen]);
};
